import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import {
  appointmentAPI, levelAPI, contractRequestAPI, agencyEnablementAPI,
} from '@/lib/api';
import {
  CONTRACT_STATUS_ORDER, TERMINATION_REASONS, getErrorMessage, isKRRole,
} from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

const WAITING_PERIOD_OPTIONS = [
  { value: '60_days', label: '60 Days' },
  { value: '90_days', label: '90 Days' },
  { value: '6_months', label: '6 Months' },
  { value: 'blockout', label: 'Blockout Date' },
  { value: 'custom', label: 'Custom' },
];

// The appointment table only accepts these 4 statuses. Every other status
// stays a request-only transition and is persisted via the workflow
// endpoint. `mapToAppointmentStatus` lets us mirror a lifecycle status
// down to the appointment when meaningful (e.g. Approved / Terminated).
const APPT_STATUSES = ['Pending', 'Approved', 'Declined', 'Terminated'];
const mapToAppointmentStatus = (lifecycle) => {
  if (APPT_STATUSES.includes(lifecycle)) return lifecycle;
  if (lifecycle === 'Submitted' || lifecycle === 'In_Progress'
      || lifecycle === 'Carrier_Pending' || lifecycle === 'Missing_Information'
      || lifecycle === 'Pending_Release' || lifecycle === 'On_Hold') return 'Pending';
  return null;
};
const isReleaseStatus = (s) => s === 'Pending_Release' || s === 'Release_Pending';

const addDaysISO = (iso, days) => {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  } catch { return ''; }
};
const computeDue = ({ release_sent_date, waiting_period_type, blockout_date }) => {
  if (waiting_period_type === 'blockout') return blockout_date || '';
  if (!release_sent_date || !waiting_period_type) return '';
  if (waiting_period_type === '60_days') return addDaysISO(release_sent_date, 60);
  if (waiting_period_type === '90_days') return addDaysISO(release_sent_date, 90);
  if (waiting_period_type === '6_months') return addDaysISO(release_sent_date, 183);
  return '';
};

const EMPTY = {
  carrier_id: '',
  contract_type_id: '',
  level_id: '',
  status: 'Pending',
  status_reason: '',
  status_comment: '',
  writing_number: '',
  appointed_date: '',
  terminated_date: '',
  termination_reason: '',
  request_notes: '',
  notes_internal: '',
  release_required: true,
  release_sent: false,
  release_sent_date: '',
  waiting_period_type: '',
  blockout_date: '',
  follow_up_date: '',
  release_notes: '',
};

/**
 * ContractManageDialog — the single dialog for adding OR managing a contract.
 *
 * Replaces the old AddContractDialog + UpdateRequestDialog + InlineRequestUpdate.
 * Covers every field previously scattered across those three:
 *   • Basic — Carrier · Contract Type · Level
 *   • Lifecycle Status — full CONTRACT_STATUS_ORDER + status reason + change comment
 *   • Appointment — Writing # · Appointed / Terminated dates · Termination reason
 *   • Release Waiting Period (only when status is Pending_Release)
 *   • Notes — Request Notes (read-only) + Internal (KR) Notes
 *
 * Save orchestration:
 *   1) if there IS an existing request → contractRequestAPI.updateStatus
 *      (workflow log + status change), and if Pending_Release
 *      contractRequestAPI.upsertReleaseFollowUp for the follow-up.
 *   2) appointmentAPI.createDirect (UPSERT) mirrors the appointment side
 *      whenever the lifecycle status maps to one of the 4 appt statuses,
 *      so writing_number / dates / notes_internal always land on the same
 *      canonical row.
 */
export const ContractManageDialog = ({
  open, onOpenChange,
  agent,
  editing = null,          // request row (from contract_requests)
  carriers = [], contractTypes = [], levels = [],
  requests = [], appointments = [],
  onSaved,
}) => {
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const [form, setForm] = useState(EMPTY);
  const [allowedLevels, setAllowedLevels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [followUpManual, setFollowUpManual] = useState(false);
  // Phase 27.17 — carriers the upline agency has actually enabled. Non-KR
  // users see ONLY these in the carrier dropdown; KR admins see every
  // carrier but non-enabled ones get a "Not enabled in agency" badge so
  // they know when they're granting an exception.
  const [enabledCarrierIds, setEnabledCarrierIds] = useState(null);

  const isEdit = !!editing;

  // Fetch the agency's enablement list when the dialog opens. Silent
  // failure falls back to "no filter applied" so a transient API blip
  // never blocks the operator.
  useEffect(() => {
    if (!open || !agent?.agency_id) { setEnabledCarrierIds(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await agencyEnablementAPI.list({ agency_id: agent.agency_id });
        if (cancelled) return;
        const ids = new Set(
          (res.data || [])
            .filter((e) => e.enabled)
            .map((e) => e.carrier_id),
        );
        setEnabledCarrierIds(ids);
      } catch { if (!cancelled) setEnabledCarrierIds(null); }
    })();
    return () => { cancelled = true; };
  }, [open, agent?.agency_id]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...EMPTY,
        carrier_id: editing.carrier_id || '',
        contract_type_id: editing.contract_type_id || '',
        level_id: editing.level_id || editing.requested_level_id || '',
        status: editing.current_status || editing.appointment_status || 'Pending',
        status_reason: '',
        status_comment: '',
        writing_number: editing.writing_number || '',
        appointed_date: (editing.appointed_date || '').split('T')[0] || '',
        terminated_date: (editing.terminated_date || '').split('T')[0] || '',
        termination_reason: editing.termination_reason || '',
        request_notes: editing.request_notes || '',
        notes_internal: editing.notes_internal || '',
      });
      setFollowUpManual(false);
    } else {
      setForm(EMPTY);
      setFollowUpManual(false);
    }
  }, [open, editing]);

  // Agency-scoped level caps whenever the operator picks a carrier.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (form.carrier_id && agent?.agency_id) {
        try {
          const res = await levelAPI.getAllowedLevels(agent.agency_id, form.carrier_id);
          if (!cancelled) setAllowedLevels(res.data);
        } catch { if (!cancelled) setAllowedLevels(null); }
      } else if (!cancelled) {
        setAllowedLevels(null);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [form.carrier_id, agent?.agency_id]);

  const filteredContractTypes = contractTypes.filter((ct) => ct.carrier_id === form.carrier_id && ct.active);
  const filteredLevels = allowedLevels?.levels
    ? [...allowedLevels.levels].sort((a, b) => b.level_rank - a.level_rank)
    : levels.filter((l) => l.carrier_id === form.carrier_id && l.active).sort((a, b) => b.level_rank - a.level_rank);

  // Business rule: one contract type per carrier. Auto-select the carrier's
  // single active contract type as soon as the carrier is picked so the
  // operator never has to touch that field. If the catalog somehow has
  // multiple, we still auto-pick the first and hide the dropdown — the
  // catalog is where multi-type carriers get resolved, not the form.
  useEffect(() => {
    if (!form.carrier_id) return;
    if (form.contract_type_id) return;
    if (filteredContractTypes.length >= 1) {
      setForm((p) => ({ ...p, contract_type_id: filteredContractTypes[0].contract_type_id }));
    }
  }, [form.carrier_id, filteredContractTypes.length]);

  const autoDue = useMemo(() => computeDue(form), [form.release_sent_date, form.waiting_period_type, form.blockout_date]);
  const effectiveDue = followUpManual ? form.follow_up_date : (autoDue || form.follow_up_date);

  // Guardrail (only when creating). Strict rule: **one contract per
  // carrier per agent**. Any non-terminal existing row blocks the add
  // outright — the operator has to Manage the existing one instead.
  const findExistingForCarrier = (carrierId) => {
    if (!carrierId) return null;
    const req = requests.find((r) => r.carrier_id === carrierId
      && !['Terminated', 'Declined'].includes(r.current_status));
    if (req) return { type: 'request', id: req.request_id, status: req.current_status };
    const appt = appointments.find((a) => a.carrier_id === carrierId
      && !['Terminated', 'Declined'].includes(a.appointment_status));
    if (appt) return { type: 'appointment', id: appt.appointment_id, status: appt.appointment_status };
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.carrier_id) { toast.error('Please select a carrier'); return; }
    // Business rule: one contract per carrier per agent. Any non-terminal
    // existing row blocks the add — Manage should be used instead.
    if (!isEdit) {
      const existing = findExistingForCarrier(form.carrier_id);
      if (existing) {
        toast.error('This agent already has a contract with this carrier. Use Manage to update it.');
        return;
      }
    }
    if (!form.level_id) { toast.error('Please select a level'); return; }
    if (form.status === 'Approved' && !form.writing_number) {
      toast.error('Writing number is required for Approved status'); return;
    }
    if (form.status === 'Terminated') {
      if (!form.terminated_date) { toast.error('Terminated date is required'); return; }
      if (!form.termination_reason) { toast.error('Termination reason is required'); return; }
    }
    if (isReleaseStatus(form.status) && form.waiting_period_type === 'blockout' && !form.blockout_date) {
      toast.error('Blockout date is required'); return;
    }

    setSaving(true);
    try {
      // 1. If editing an existing request, drive the workflow endpoint so
      //    the audit trail is captured. Skip when the status didn't
      //    actually change (avoids empty workflow rows).
      if (isEdit && editing?.request_id && form.status !== editing.current_status) {
        await contractRequestAPI.updateStatus(editing.request_id, {
          status: form.status,
          comment: form.status_comment || undefined,
          status_reason: form.status_reason || undefined,
        });
      }

      // 2. Release waiting-period follow-up (only meaningful when status
      //    is Pending_Release AND the operator filled the sub-form).
      if (isEdit && editing?.request_id
          && isReleaseStatus(form.status)
          && (form.release_sent_date || form.waiting_period_type || effectiveDue || form.release_notes)) {
        await contractRequestAPI.upsertReleaseFollowUp(editing.request_id, {
          release_required: form.release_required,
          release_sent: form.release_sent,
          release_sent_date: form.release_sent_date || null,
          waiting_period_type: form.waiting_period_type || null,
          blockout_date: form.blockout_date || null,
          follow_up_date: effectiveDue || null,
          notes: form.release_notes || form.status_comment || null,
        });
      }

      // 3. UPSERT the appointment side so writing_number, dates and
      //    notes_internal always land on the canonical row. The lifecycle
      //    status usually maps to an appointment status; when it does NOT
      //    (Waiting_On_Agent, Closed) a brand-new contract still MUST be
      //    persisted, so fall back to 'Pending' and record the real
      //    lifecycle status via the workflow endpoint below. Without this
      //    fallback the add showed a success toast but saved nothing.
      const apptStatus = mapToAppointmentStatus(form.status);
      const effectiveApptStatus = apptStatus || (!isEdit ? 'Pending' : null);
      let newRequestId = null;
      if (effectiveApptStatus) {
        const res = await appointmentAPI.createDirect({
          npn: agent.npn,
          carrier_id: form.carrier_id,
          contract_type_id: form.contract_type_id || null,
          level_id: form.level_id,
          appointment_status: effectiveApptStatus,
          writing_number: form.writing_number || null,
          appointed_date: form.appointed_date || null,
          terminated_date: form.terminated_date || null,
          termination_reason: form.termination_reason || null,
          notes_internal: form.notes_internal || null,
          // Phase 27.8 — always create the request + workflow row for a
          // new contract so every add produces the same audit trail as
          // manage. The old toggle is gone: audit history is not
          // optional in the unified module.
          create_request_record: !isEdit,
        });
        newRequestId = res?.data?.appointment?.request_id || null;
      }

      // 4. For a NEW contract, the appointment side can only hold one of
      //    the 4 appt statuses, so createDirect recorded `effectiveApptStatus`
      //    (often 'Pending' for lifecycle statuses like Submitted / In_Progress
      //    / Carrier_Pending / Missing_Information / On_Hold / Pending_Release).
      //    Stamp the REAL lifecycle status + reason on the request whenever it
      //    differs from what was recorded, or whenever the operator entered a
      //    status reason — otherwise the contract wrongly showed as "Pending"
      //    and the reason was silently dropped.
      if (!isEdit && newRequestId
          && (form.status !== effectiveApptStatus || form.status_reason)) {
        await contractRequestAPI.updateStatus(newRequestId, {
          status: form.status,
          status_reason: form.status_reason || undefined,
          // A comment forces the workflow endpoint to persist a row even
          // when the status is unchanged, so the reason is never a no-op.
          comment: form.status_reason
            ? `Reason: ${form.status_reason}`
            : 'Status set on contract creation',
        });
      }

      toast.success(isEdit ? 'Contract updated' : 'Contract created');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save contract'));
    } finally {
      setSaving(false);
    }
  };

  const existingBlock = !isEdit && form.carrier_id ? findExistingForCarrier(form.carrier_id) : null;
  const existingCarrier = existingBlock ? carriers.find((c) => c.carrier_id === form.carrier_id) : null;
  const contractTypeName = form.contract_type_id
    ? (filteredContractTypes.find((ct) => ct.contract_type_id === form.contract_type_id)?.contract_type_name || '—')
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="contract-manage-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? 'Manage Contract' : 'Add Contract'}
          </DialogTitle>
          {isEdit && (
            <DialogDescription>
              {editing?.carrier_name || '—'} · {editing?.contract_type_name || '—'} · {editing?.level_name || '—'}
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 py-2">

          {/* Hard block when adding a duplicate carrier — one contract
              per carrier per agent (business rule). */}
          {existingBlock && (
            <Alert className="bg-amber-50 border-amber-300" data-testid="existing-carrier-block">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>{existingCarrier?.carrier_name} already has a contract</strong> for this agent
                (status: {existingBlock.status.replace(/_/g, ' ')}). Only one contract per carrier is allowed —
                use <em>Manage</em> on the existing row to update it.
              </AlertDescription>
            </Alert>
          )}

          {/* ── Basic ─────────────────────────────────────────── */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Basic</h3>
            <div className="space-y-1.5">
              <Label>Carrier *{isEdit && <Lock size={10} className="inline ml-1 text-slate-400" />}</Label>
              <Select value={form.carrier_id} disabled={isEdit}
                onValueChange={(v) => setForm((p) => ({ ...p, carrier_id: v, contract_type_id: '', level_id: '' }))}
              >
                <SelectTrigger data-testid="carrier-select"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    // Phase 27.17 — narrow the carrier list to what the
                    // upline agency has actually enabled. KR admins see
                    // every carrier so they can still grant exceptions,
                    // just with a badge indicating the gap.
                    const available = carriers.filter((c) => {
                      if (!c.active) return false;
                      if (!enabledCarrierIds) return true;           // still loading — no filter yet
                      if (isKR) return true;                          // KR sees all
                      return enabledCarrierIds.has(c.carrier_id);
                    });
                    if (available.length === 0 && !isKR) {
                      return (
                        <div className="px-2 py-3 text-xs text-slate-500 italic" data-testid="no-enabled-carriers">
                          No carriers are enabled for this agency. Ask KR to enable a carrier first.
                        </div>
                      );
                    }
                    return available.map((c) => {
                      // Only show the "not enabled" badge on ADD flow —
                      // in edit mode the carrier is locked anyway so
                      // the badge would just be visual noise.
                      const notEnabled = !isEdit && isKR && enabledCarrierIds && !enabledCarrierIds.has(c.carrier_id);
                      const hasExisting = !isEdit && !!findExistingForCarrier(c.carrier_id);
                      return (
                        <SelectItem key={c.carrier_id} value={c.carrier_id} data-testid={`carrier-opt-${c.carrier_id}`}>
                          <span className="inline-flex items-center gap-2">
                            <span>{c.carrier_name}</span>
                            {notEnabled && (
                              <Badge variant="outline" className="text-[9px] py-0 h-4 border-red-300 bg-red-50 text-red-700" data-testid={`not-enabled-badge-${c.carrier_id}`}>
                                Not enabled in agency
                              </Badge>
                            )}
                            {hasExisting && (
                              <Badge variant="outline" className="text-[9px] py-0 h-4 border-amber-300 bg-amber-50 text-amber-700" data-testid={`existing-badge-${c.carrier_id}`}>
                                Existing Contract
                              </Badge>
                            )}
                          </span>
                        </SelectItem>
                      );
                    });
                  })()}
                </SelectContent>
              </Select>
              {/* Contract Type is auto-selected from the carrier catalog
                  (one type per carrier). Show it read-only for context. */}
              {contractTypeName && (
                <p className="text-xs text-slate-500" data-testid="auto-contract-type-hint">
                  Contract type: <span className="font-medium text-slate-700">{contractTypeName}</span>
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Level *</Label>
              <Select value={form.level_id} disabled={!form.carrier_id}
                onValueChange={(v) => setForm((p) => ({ ...p, level_id: v }))}
              >
                <SelectTrigger data-testid="level-select"><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent>
                  {filteredLevels.map((l) => <SelectItem key={l.level_id} value={l.level_id}>{l.level_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {allowedLevels?.cap_enforced && (
                <p className="text-xs text-amber-600">Available levels are limited by the agency&apos;s level cap.</p>
              )}
            </div>
          </section>

          <Separator />

          {/* ── Lifecycle Status ──────────────────────────────── */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Lifecycle Status</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Status *</Label>
                <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTRACT_STATUS_ORDER.map((s) => (
                      <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Status Reason</Label>
                <Input value={form.status_reason}
                  onChange={(e) => setForm((p) => ({ ...p, status_reason: e.target.value }))}
                  placeholder="Brief reason (optional)"
                />
              </div>
            </div>
            {isEdit && (
              <div className="space-y-1.5">
                <Label>Change Comment</Label>
                <Textarea rows={2} value={form.status_comment}
                  onChange={(e) => setForm((p) => ({ ...p, status_comment: e.target.value }))}
                  placeholder="Audit note for this update…"
                />
              </div>
            )}
          </section>

          {/* ── Appointment fields ────────────────────────────── */}
          <Separator />
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Appointment</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Writing Number{form.status === 'Approved' && <span className="text-red-500"> *</span>}</Label>
                <Input value={form.writing_number}
                  onChange={(e) => setForm((p) => ({ ...p, writing_number: e.target.value }))}
                  placeholder="e.g. WN-A1B2C3"
                  data-testid="writing-number-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Appointed Date</Label>
                <Input type="date" value={form.appointed_date}
                  onChange={(e) => setForm((p) => ({ ...p, appointed_date: e.target.value }))}
                />
              </div>
            </div>
            {form.status === 'Terminated' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Terminated Date *</Label>
                  <Input type="date" value={form.terminated_date}
                    onChange={(e) => setForm((p) => ({ ...p, terminated_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Termination Reason *</Label>
                  <Select value={form.termination_reason || '__none__'}
                    onValueChange={(v) => setForm((p) => ({ ...p, termination_reason: v === '__none__' ? '' : v }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select reason</SelectItem>
                      {TERMINATION_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </section>

          {/* ── Release Waiting Period (Pending_Release only) ── */}
          {isReleaseStatus(form.status) && (
            <>
              <Separator />
              <section className="rounded-lg border border-orange-200 bg-orange-50/40 p-3 space-y-3" data-testid="release-section">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-orange-700">Release Waiting Period</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 rounded-lg border bg-white p-2.5">
                    <Switch checked={form.release_required}
                      onCheckedChange={(v) => setForm((p) => ({ ...p, release_required: v }))}
                    />
                    <Label>Release Required</Label>
                  </div>
                  <div className="flex items-center gap-2 rounded-lg border bg-white p-2.5">
                    <Switch checked={form.release_sent}
                      onCheckedChange={(v) => setForm((p) => ({ ...p, release_sent: v }))}
                    />
                    <Label>Release Sent</Label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Release Sent Date</Label>
                    <Input type="date" value={form.release_sent_date}
                      onChange={(e) => { setForm((p) => ({ ...p, release_sent_date: e.target.value })); setFollowUpManual(false); }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Waiting Period Type</Label>
                    <Select value={form.waiting_period_type || 'none'}
                      onValueChange={(v) => { setForm((p) => ({ ...p, waiting_period_type: v === 'none' ? '' : v })); setFollowUpManual(false); }}
                    >
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {WAITING_PERIOD_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {form.waiting_period_type === 'blockout' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Blockout Date *</Label>
                    <Input type="date" value={form.blockout_date}
                      onChange={(e) => { setForm((p) => ({ ...p, blockout_date: e.target.value })); setFollowUpManual(false); }}
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Follow-Up Due Date <Badge variant="outline" className="text-[10px] ml-1">{!followUpManual && autoDue ? 'auto' : 'manual'}</Badge>
                  </Label>
                  <Input type="date" value={effectiveDue}
                    onChange={(e) => { setForm((p) => ({ ...p, follow_up_date: e.target.value })); setFollowUpManual(true); }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Release Notes</Label>
                  <Textarea rows={2} value={form.release_notes}
                    onChange={(e) => setForm((p) => ({ ...p, release_notes: e.target.value }))}
                    placeholder="Context, current FMO/upline, expected process…"
                  />
                </div>
              </section>
            </>
          )}

          {/* ── Notes ─────────────────────────────────────────── */}
          <Separator />
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</h3>
            {form.request_notes && (
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1">
                  Request Notes <Lock size={10} className="text-slate-400" />
                </Label>
                <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-2 whitespace-pre-wrap text-slate-700">
                  {form.request_notes}
                </div>
                <p className="text-[10px] text-slate-400">Captured when the request was created — read-only.</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Internal Notes</Label>
              <Textarea rows={3} value={form.notes_internal}
                onChange={(e) => setForm((p) => ({ ...p, notes_internal: e.target.value }))}
                placeholder="KR-only notes…"
              />
            </div>
          </section>

          {/* Phase 27.8 — the "Create Request + Workflow Record" toggle
              was removed. In the unified Contracts module every new
              contract now writes a workflow row automatically so add
              and manage share the same audit trail. */}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || !!existingBlock} data-testid="save-contract-btn">
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Contract')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContractManageDialog;
